/**
 * Medição da publicidade própria: `house_ad_impression` e `house_ad_click`.
 *
 * Usa o endpoint de analytics que já existe, `POST /api/track/event` (coleção
 * `analytics_events`), com `source: 'house_ad'`. Nenhuma rota nova no backend.
 * Separação semântica pelo `event_type`:
 *   - não é AdSense: nada daqui toca o bloco nem conta anúncio do Google;
 *   - não é a coleta do IntelligenceEngine: os leitores de `analytics_events` só
 *     consideram deal_view/deal_detail_click/deal_buy_click, e estes eventos não têm
 *     `deal_id`;
 *   - não é venda nem conversão: só impressão e clique no anúncio interno.
 *
 * Só envia com build de produção, no domínio de produção, fora de automação
 * (navigator.webdriver) e fora da equipe (`localStorage sv-interno=1`). Em localhost,
 * preview e testes nada sai do navegador.
 *
 * Deduplicação de impressão: sessão + campanha + placement + posição, em janela de
 * 30 min (a mesma da coleta de exposição), guardada no sessionStorage para sobreviver a
 * recarregar a página. Clique: só evento confiável do navegador (não sintético), um por
 * toque.
 *
 * Sessão anônima: 32 hex aleatórios no sessionStorage (`sv-pub-sessao`), some ao fechar a
 * aba. Nada de conta, e-mail ou aparelho.
 */
import { API } from '../config/apiBase';
import { adsenseLiberado } from '../config/adsense';

export const ROTA_EVENTOS = '/track/event';
export const ORIGEM_EVENTO = 'house_ad';
export const JANELA_IMPRESSAO_MS = 30 * 60 * 1000;
const CHAVE_SESSAO = 'sv-pub-sessao';
const CHAVE_VISTOS = 'sv-pub-vistos';

const estado = { enviar: null, liberado: null, sessaoMemoria: null, vistosMemoria: {} };

const hex = (n) => {
    const b = new Uint8Array(n);
    if (typeof crypto !== 'undefined' && crypto.getRandomValues) crypto.getRandomValues(b);
    else for (let i = 0; i < n; i += 1) b[i] = Math.floor(Math.random() * 256);
    return Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
};

function lerSessao(chave) {
    try { return window.sessionStorage.getItem(chave); } catch (_) { return null; }
}

function gravarSessao(chave, valor) {
    try { window.sessionStorage.setItem(chave, valor); } catch (_) { /* segue em memória */ }
}

export function sessaoPublicidade() {
    let id = lerSessao(CHAVE_SESSAO);
    if (!/^[a-f0-9]{32}$/.test(id || '')) id = estado.sessaoMemoria;
    if (!/^[a-f0-9]{32}$/.test(id || '')) id = hex(16);
    estado.sessaoMemoria = id;
    gravarSessao(CHAVE_SESSAO, id);
    return id;
}

function lerVistos() {
    try {
        const salvo = JSON.parse(lerSessao(CHAVE_VISTOS) || 'null');
        if (salvo && typeof salvo === 'object') return { ...salvo, ...estado.vistosMemoria };
    } catch (_) { /* corrompido: recomeça */ }
    return { ...estado.vistosMemoria };
}

/** true se a impressão é nova na janela; já registra a chave. */
function impressaoNova(chave, agora) {
    const vistos = lerVistos();
    if (vistos[chave] && agora - vistos[chave] < JANELA_IMPRESSAO_MS) return false;
    vistos[chave] = agora;
    Object.keys(vistos).forEach((k) => { if (agora - vistos[k] >= JANELA_IMPRESSAO_MS) delete vistos[k]; });
    estado.vistosMemoria = vistos;
    gravarSessao(CHAVE_VISTOS, JSON.stringify(vistos));
    return true;
}

function enviarPadrao(corpo) {
    try {
        fetch(`${API}${ROTA_EVENTOS}`, {
            method: 'POST', body: corpo, keepalive: true, headers: { 'Content-Type': 'application/json' },
        }).catch(() => {});
    } catch (_) { /* sem rede: o evento se perde */ }
}

function equipe() {
    try { return window.localStorage.getItem('sv-interno') === '1'; } catch (_) { return false; }
}

/** Critério único de envio. Injetável só nos testes. */
export function rastreioLiberado() {
    if (typeof window === 'undefined') return false;
    if (estado.liberado !== null) return estado.liberado;
    return process.env.NODE_ENV === 'production'
        && adsenseLiberado()
        && !(typeof navigator !== 'undefined' && navigator.webdriver === true)
        && !equipe();
}

/**
 * Registra um evento de anúncio interno. Devolve false quando não enviou (fora de produção,
 * tipo desconhecido, impressão repetida na janela ou clique não confiável).
 */
export function registrarHouseAd(tipo, dados, { confiavel = true } = {}) {
    if (tipo !== 'house_ad_impression' && tipo !== 'house_ad_click') return false;
    if (!rastreioLiberado()) return false;
    const { campaign_id: campanha, store_id: loja, placement, position } = dados || {};
    if (!campanha || !loja || !placement) return false;
    const agora = Date.now();
    const sessao = sessaoPublicidade();
    if (tipo === 'house_ad_click' && !confiavel) return false;
    if (tipo === 'house_ad_impression' && !impressaoNova(`${sessao}|${campanha}|${placement}|${position}`, agora)) {
        return false;
    }
    const corpo = {
        event_type: tipo,
        source: ORIGEM_EVENTO,
        metadata: {
            campaign_id: campanha,
            store_id: loja,
            placement,
            position,
            anonymous_session: sessao,
            timestamp: new Date(agora).toISOString(),
        },
    };
    (estado.enviar || enviarPadrao)(JSON.stringify(corpo));
    return true;
}

/** Só para testes. */
export function _configurarRastreio({ enviar = null, liberado = null } = {}) {
    estado.enviar = enviar;
    estado.liberado = liberado;
    estado.sessaoMemoria = null;
    estado.vistosMemoria = {};
    try {
        window.sessionStorage.removeItem(CHAVE_SESSAO);
        window.sessionStorage.removeItem(CHAVE_VISTOS);
    } catch (_) { /* nada */ }
}
